/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "pages/dashboard/data/reportsBarChartData";
import reportsLineChartData from "pages/dashboard/data/reportsLineChartData";

// Dashboard components
import Projects from "pages/dashboard/components/Projects";
import OrdersOverview from "pages/dashboard/components/OrdersOverview";
import FreshReworkBarChart from "examples/Charts/BarCharts/FreshReworkBarChart";
import ActivityTypeBarChart from "examples/Charts/BarCharts/ActivityTypeBarChart";
import ActivityEfficiencyBarChart from "examples/Charts/BarCharts/ActivityEfficiencyBarChart";
import WaitingHoursBarChart from "examples/Charts/BarCharts/WaitingHoursBarChart";
import { dashboardData } from "actions/dashboard";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDepartmentList } from "actions/department";
import { getShiftList } from "actions/shift";
import MDButton from "components/MDButton";
import { Card, Grid, TextField, Autocomplete, Icon } from "@mui/material";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { DASHBOARD_STATUS } from "components/common/constant";
import { DATE_RANGE_OPTIONS } from "components/common/constant";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";

function Dashboard() {
  const { notify } = useNotify();
  const dispatch = useDispatch();
  const [activityTypeChartData, setActivityTypeChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Activity %",
        backgroundColor: "#156082",
        data: [],
      },
    ],
  });
  const [efficiencyChartData, setEfficiencyChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Efficiency %",
        backgroundColor: "#156082",
        data: [],
      },
    ],
  });
  const [waitingHoursChartData, setWaitingHoursChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Hours",
        backgroundColor: "#156082",
        data: [],
      },
    ],
  });
  const [reworkChartData, setReworkChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Rework %",
        backgroundColor: "#156082",
        data: [],
      },
    ],
  });
  const [idleHoursChartData, setIdleHoursChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Hours",
        backgroundColor: "#156082",
        data: [],
      },
    ],
  });
  const [freshReworkChartData, setFreshReworkChartData] = useState({
    labels: ["Hours"],
    datasets: [],
  });
  const [adoptionRateChartData, setAdoptionRateChartData] = useState({
    labels: ["Adoption %"],
    datasets: [],
  });
  const [dutyTypeChartData, setDutyTypeChartData] = useState({
    labels: ["Duty Type"],
    datasets: [],
  });
  const [filters, setFilters] = useState(() => {
    const today = new Date();

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);

    return {
      dateRange: "7",
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
      departmentIDs: [],
      shiftIDs: [],
      // status: "ACTIVE",
      isSearched: false,
    };
  });
  const [listState, setListState] = useState({
    showLoaderOnClick: false,
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: filters.startDate,
    endDate: filters.endDate,
  });

  const shiftListData = useSelector((state) => state.shift.shiftList);

  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );

  useEffect(() => {
    getDashboardData();
  }, []);

  useEffect(() => {
    getDepartmentList(dispatch);
    getShiftList(dispatch);
  }, [dispatch]);

  const getDateRange = (days) => {
    const today = new Date();

    if (days === "TODAY") {
      const todayString = dayjs(today).format("YYYY-MM-DD");

      return {
        startDate: todayString,
        endDate: todayString,
      };
    }

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    if (days === "1") {
      const yesterdayString = dayjs(endDate).format("YYYY-MM-DD");

      return {
        startDate: yesterdayString,
        endDate: yesterdayString,
      };
    }

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (Number(days) - 1));

    return {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
    };
  };

  const departmentOptions =
    departmentListData?.data?.Department?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const shiftOptions =
    shiftListData?.data?.shift?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const getDashboardData = async (customPayload = null) => {
    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      const payload = customPayload || {
        startDate: filters.startDate,
        endDate: filters.endDate,
        shiftID: filters.shiftIDs,
        departmentID: filters.departmentIDs,
        // status: filters.status,
      };

      const response = await dashboardData(payload);

      if (response?.code !== 200) {
        notify(response?.message || "Failed to fetch dashboard data", "error");
        return;
      }

      const activityData = response?.data?.activity_report ?? [];

      setActivityTypeChartData({
        labels: activityData.map((item) => item.activity_type),
        datasets: [
          {
            label: "Activity %",
            backgroundColor: "#156082",
            data: activityData.map((item) => item.percentage),
          },
        ],
      });
      const efficiencyData = response?.data?.efficiency_report ?? [];

      setEfficiencyChartData({
        labels: efficiencyData.map((item) => item.department_name),
        datasets: [
          {
            label: "Efficiency %",
            backgroundColor: "#156082",
            data: efficiencyData.map((item) => Number(item.efficiency_rate)),
          },
        ],
      });

      const reworkData = response?.data?.rework_report ?? [];

      setReworkChartData({
        labels: reworkData.map((item) => item.department_name),
        datasets: [
          {
            label: "Rework %",
            backgroundColor: "#156082",
            data: reworkData.map((item) => Number(item.rework_rate)),
          },
        ],
      });

      const waitingData = response?.data?.waiting_report ?? [];

      setWaitingHoursChartData({
        labels: waitingData.map((item) => item.sub_activity_name),
        datasets: [
          {
            label: "Hours",
            backgroundColor: "#156082",
            data: waitingData.map((item) => Number(item.hours)),
          },
        ],
      });

      const idleData = response?.data?.idle_report ?? [];

      setIdleHoursChartData({
        labels: idleData.map((item) => item.sub_activity_name),
        datasets: [
          {
            label: "Hours",
            backgroundColor: "#156082",
            data: idleData.map((item) => Number(item.hours)),
          },
        ],
      });

      const activityHoursData = response?.data?.activity_hours_report ?? [];

      const freshDepartmentData =
        activityHoursData.find((item) => item.activity_type === "FRESH")
          ?.departmentwise ?? [];

      const reworkDepartmentData =
        activityHoursData.find((item) => item.activity_type === "REWORK")
          ?.departmentwise ?? [];

      const labels = [
        ...new Set([
          ...freshDepartmentData.map((item) => item.department_name),
          ...reworkDepartmentData.map((item) => item.department_name),
        ]),
      ];

      setFreshReworkChartData({
        labels,
        datasets: [
          {
            label: "Fresh",
            backgroundColor: "#156082",
            data: labels.map((department) => {
              const item = freshDepartmentData.find(
                (d) => d.department_name === department,
              );
              return item ? Number(item.hours) : 0;
            }),
          },
          {
            label: "Rework",
            backgroundColor: "#ED7D31",
            data: labels.map((department) => {
              const item = reworkDepartmentData.find(
                (d) => d.department_name === department,
              );
              return item ? Number(item.hours) : 0;
            }),
          },
        ],
      });

      const adoptionData = response?.data?.adoptionRateReport ?? [];

      const colors = [
        "#156082",
        "#ED7D31",
        "#2E7D32",
        "#29B6F6",
        "#AB47BC",
        "#8BC34A",
        "#0D5C7D",
        "#F57C20",
        "#4CAF50",
        "#9C27B0",
        "#795548",
        "#607D8B",
      ];

      setAdoptionRateChartData({
        labels: adoptionData.map((item) => item.departmentName),
        datasets: [
          {
            label: "Adoption %",
            backgroundColor: adoptionData.map(
              (_, index) => colors[index % colors.length],
            ),
            data: adoptionData.map((item) => Number(item.adoptionRate)),
          },
        ],
      });

      const dutyTypeData = response?.data?.dutyTypeReport ?? [];

      const dutyTypeColors = {
        Regular: "#156082",
        "Half Day": "#2E7D32",
        Outdoor: "#ED7D31",
        Overtime: "#29B6F6",
      };

      setDutyTypeChartData({
        labels: dutyTypeData.map((item) => item.shift),

        datasets: [
          {
            label: "Regular",
            backgroundColor: "#156082",
            data: dutyTypeData.map((item) => Number(item.regular || 0)),
          },
          {
            label: "Out Door",
            backgroundColor: "#ED7D31",
            data: dutyTypeData.map((item) => Number(item.outdoor || 0)),
          },
          {
            label: "Half Day",
            backgroundColor: "#2E7D32",
            data: dutyTypeData.map((item) => Number(item.halfDay || 0)),
          },
          {
            label: "OT",
            backgroundColor: "#29B6F6",
            data: dutyTypeData.map((item) => Number(item.ot || 0)),
          },
        ],
      });
    } catch (error) {
      notify(error?.message || "Something went wrong", "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const handleSearch = () => {
    setAppliedDateRange({
      startDate: filters.startDate,
      endDate: filters.endDate,
    });

    getDashboardData();

    setFilters((prev) => ({
      ...prev,
      isSearched: true,
    }));
  };

  const handleClear = () => {
    const today = new Date();

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);

    const resetFilters = {
      dateRange: "7",
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
      departmentIDs: [],
      shiftIDs: [],
      // status: filters.status,
      isSearched: false,
    };

    setAppliedDateRange({
      startDate: resetFilters.startDate,
      endDate: resetFilters.endDate,
    });

    setFilters(resetFilters);

    getDashboardData({
      startDate: resetFilters.startDate,
      endDate: resetFilters.endDate,
      departmentIDs: [],
      shiftIDs: [],
      // status: filters.status,
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/*This code can be used in future to show cards*/}
        {/* <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="weekend"
                title="Bookings"
                count={281}
                percentage={{
                  color: "success",
                  amount: "+55%",
                  label: "than lask week",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="leaderboard"
                title="Today's Users"
                count="2,300"
                percentage={{
                  color: "success",
                  amount: "+3%",
                  label: "than last month",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="store"
                title="Revenue"
                count="34k"
                percentage={{
                  color: "success",
                  amount: "+1%",
                  label: "than yesterday",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="secondary"
                icon="person_add"
                title="Followers"
                count="+91"
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Just updated",
                }}
              />
            </MDBox>
          </Grid>
        </Grid> */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={DATE_RANGE_OPTIONS}
                      value={
                        DATE_RANGE_OPTIONS.find(
                          (item) => item.value === filters.dateRange,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        if (!value) return;

                        if (value.value === "CUSTOM") {
                          setFilters({
                            ...filters,
                            dateRange: "CUSTOM",
                          });
                        } else {
                          const dates = getDateRange(value.value);

                          setFilters({
                            ...filters,
                            dateRange: value.value,
                            startDate: dates.startDate,
                            endDate: dates.endDate,
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date Range"
                        />
                      )}
                    />
                  </Grid>
                  {filters.dateRange === "CUSTOM" && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            value={dayjs(filters.startDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                startDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            minDate={dayjs(filters.startDate)}
                            label="End Date"
                            value={dayjs(filters.endDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                endDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={departmentOptions}
                      value={
                        departmentOptions.find(
                          (item) => item.id === filters.departmentIDs?.[0],
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          departmentIDs: value ? [value.id] : [],
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={shiftOptions}
                      value={
                        shiftOptions.find(
                          (item) => item.id === filters.shiftIDs?.[0],
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          shiftIDs: value ? [value.id] : [],
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Shift" />
                      )}
                    />
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={DASHBOARD_STATUS}
                      value={
                        DASHBOARD_STATUS.find(
                          (item) => item.value === filters.status,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          status: value?.value || "ACTIVE",
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Status" />
                      )}
                    />
                  </Grid> */}
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={filters.dateRange === "CUSTOM" ? 2 : 3}
                    xl={2}
                  >
                    <MDBox
                      display="flex"
                      flexDirection={
                        filters.dateRange === "CUSTOM" ? "column" : "row"
                      }
                      gap={1}
                      width="100%"
                      alignItems="flex-start"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          minWidth: "120px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.isSearched && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            minWidth: "120px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <Grid container spacing={3} mt={0.3}>
          <Grid item xs={4}>
            <Card>
              <MDBox px={3} py={1.5}>
                <Typography variant="body2" fontWeight="bold">
                  {
                    DATE_RANGE_OPTIONS.find(
                      (item) => item.value === filters.dateRange,
                    )?.label
                  }
                </Typography>

                <Typography variant="caption" color="text">
                  {appliedDateRange.startDate === appliedDateRange.endDate
                    ? dayjs(appliedDateRange.startDate).format("DD-MMM-YYYY")
                    : `${dayjs(appliedDateRange.startDate).format("DD-MMM-YYYY")} to ${dayjs(
                        appliedDateRange.endDate,
                      ).format("DD-MMM-YYYY")}`}
                </Typography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="light"
                  title="Adoption Rate"
                  //description cab be used in future to show some insights on charts
                  // description="Last Campaign Performance"
                  // date="campaign sent 2 days ago"
                  chart={adoptionRateChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <FreshReworkBarChart
                  color="light"
                  title="Duty Type"
                  description=""
                  date=""
                  chart={dutyTypeChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <ActivityTypeBarChart
                  color="light"
                  title="Activity Type Distribution"
                  description=""
                  date=""
                  chart={activityTypeChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3}>
                <ActivityEfficiencyBarChart
                  color="light"
                  title="Efficiency %"
                  description=""
                  date=""
                  chart={efficiencyChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <WaitingHoursBarChart
                  color="light"
                  title="Waiting Hours"
                  description=""
                  date=""
                  chart={waitingHoursChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <WaitingHoursBarChart
                  color="light"
                  title="Idle Hours"
                  description=""
                  date=""
                  chart={idleHoursChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3}>
                <FreshReworkBarChart
                  color="light"
                  title="Fresh vs Rework Hours"
                  description=""
                  date=""
                  showHours
                  chart={freshReworkChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3}>
                <ActivityEfficiencyBarChart
                  color="light"
                  title="Rework %"
                  description=""
                  date=""
                  chart={reworkChartData}
                />
              </MDBox>
            </Grid>

            {/* report line charts can be used in future if there is any requirement for more charts by client */}
            {/* <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="daily sales"
                  description={
                    <>
                      (<strong>+15%</strong>) increase in today sales.
                    </>
                  }
                  date="updated 4 min ago"
                  chart={sales}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="dark"
                  title="completed tasks"
                  description="Last Campaign Performance"
                  date="just updated"
                  chart={tasks}
                />
              </MDBox>
            </Grid> */}
          </Grid>
        </MDBox>
        {/* <MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={8}>
              <Projects />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <OrdersOverview />
            </Grid>
          </Grid>
        </MDBox> */}
      </MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
